import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/SEOHead';
import QuoteFormWidget from '../../../components/feature/QuoteFormWidget';

const SolarBlogPost = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Solar Electrical Connections: What Fresno Homeowners Must Know Before Installing',
    description: 'Planning solar panels in Fresno? Your electrician is just as important as your solar installer. Learn what electrical work is required and how to qualify for CA rebates.',
    datePublished: '2025-01-05',
    dateModified: '2025-01-05',
    author: { '@type': 'Organization', name: 'Electrician Solutions Fresno' },
    publisher: { '@type': 'Organization', name: 'Electrician Solutions Fresno', url: 'https://electriciansolutionsfresno.com' },
    mainEntityOfPage: 'https://electriciansolutionsfresno.com/blog/solar-electrical-connections-fresno',
  };

  return (
    <>
      <SEOHead
        title="Solar Electrical Connections Fresno CA | Solar Panel Wiring Guide | Electrician Solutions"
        description="Planning solar panels in Fresno? Your electrician is just as important as your solar installer. Learn what electrical work is required and how to qualify for CA rebates."
        canonical="/blog/solar-electrical-connections-fresno"
        keywords="solar panel electrical Fresno, solar inverter installation Fresno CA, solar wiring Fresno, solar electrician Fresno"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-28 pb-0 bg-[#1a1a1a]">
          <div className="max-w-[900px] mx-auto px-6 pt-6 pb-10">
            <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
              <Link to="/" className="hover:text-[#FFD700] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-[#FFD700] transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-400">EV & Solar</span>
            </nav>
            <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">EV & Solar</span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mb-5 leading-tight">
              Solar Electrical Connections: What Fresno Homeowners Must Know Before Installing
            </h1>
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <span>January 5, 2025</span><span>·</span><span>7 min read</span><span>·</span>
              <span className="text-[#FFD700]">Electrician Solutions Fresno</span>
            </div>
          </div>
        </section>

        <section className="max-w-[900px] mx-auto px-6 -mt-2">
          <div className="rounded-2xl overflow-hidden h-72 sm:h-[440px]">
            <img
              src="https://public.readdy.ai/ai/img_res/43bdfd5e10a21d3347d8b40c7aca9e1b.jpg"
              alt="Solar panel electrical connections Fresno CA"
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        <article className="max-w-[900px] mx-auto px-6 py-12">
          <p className="text-gray-600 text-lg leading-relaxed mb-8">
            Fresno gets 270+ sunny days per year, making it one of the best solar markets in California. But before panels go on your roof, the electrical work is critical — and often overlooked. Your solar installer puts panels up; your licensed Fresno electrician handles the inverter, panel upgrade, battery connections, and the critical PG&E interconnection that makes net metering work.
          </p>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">What Electrical Work Is Needed for Solar in Fresno?</h2>
          <div className="space-y-4 mb-8">
            {[
              { title: 'Panel Capacity Assessment', desc: 'Before solar installation, your electrical panel must have sufficient capacity. Most Fresno homes with 100A panels need a 200A upgrade before adding solar. We assess your panel and let you know if an upgrade is required.' },
              { title: 'Dedicated Solar Circuit & Breakers', desc: 'A dedicated circuit must be run from your solar inverter to your main panel. This requires pulling a Fresno City permit and coordinating with PG&E for the interconnection agreement.' },
              { title: 'Inverter Installation & Wiring', desc: 'Your solar inverter converts DC power from panels to AC power for your home. String inverters, microinverters, and hybrid inverters all require different electrical connections. We handle all types.' },
              { title: 'Battery Storage Wiring', desc: 'Adding a Tesla Powerwall, Enphase IQ Battery, or SolarEdge battery to your Fresno solar system requires its own dedicated circuit, disconnect switch, and communication wiring.' },
              { title: 'PG&E Interconnection & Net Metering', desc: 'To sell excess solar power back to PG&E under NEM 3.0, your system must pass a PG&E inspection. Our licensed Fresno electricians ensure all work meets their requirements on the first visit.' },
            ].map((item, i) => (
              <div key={i} className="p-5 bg-[#f5f5f5] rounded-xl">
                <h3 className="font-black text-[#1a1a1a] text-sm mb-1 flex items-center gap-2">
                  <i className="ri-sun-line text-[#FFD700] text-lg"></i>
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed ml-6">{item.desc}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">California Solar Incentives for Fresno Homeowners in 2025</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1a1a1a] text-white">
                  <th className="text-left px-4 py-3 font-bold rounded-tl-lg">Incentive</th>
                  <th className="text-left px-4 py-3 font-bold">Value</th>
                  <th className="text-left px-4 py-3 font-bold rounded-tr-lg">Notes</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Federal Solar Tax Credit (ITC)', '30% of total system cost', 'Claimed on federal taxes, expires 2032'],
                  ['California NEM 3.0', 'Export credits for excess power', 'Must interconnect with PG&E to qualify'],
                  ['SGIP Battery Storage Rebate', '$150–$200/kWh of battery capacity', 'Apply before installation through PG&E'],
                  ['Property Tax Exclusion', 'Solar adds value, no property tax increase', 'CA Code Section 73 exclusion'],
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f5f5]'}>
                    <td className="px-4 py-3 font-medium text-[#1a1a1a]">{row[0]}</td>
                    <td className="px-4 py-3 text-gray-600">{row[1]}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Why Your Solar Installer Isn't Your Electrician</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Most solar installers in Fresno are C-46 Solar Contractors — not C-10 Electrical Contractors. They can install panels and inverters, but complex panel upgrades, battery storage wiring, and PG&E interconnection work requires a licensed C-10 electrician like Electrician Solutions Fresno.
          </p>
          <p className="text-gray-600 leading-relaxed mb-8">
            If you're pairing solar with an <Link to="/services/renewables-ev/ev-charger-installation" className="text-[#FFD700] font-bold hover:underline">EV charger installation</Link> or <Link to="/services/renewables-ev/backup-battery-systems" className="text-[#FFD700] font-bold hover:underline">home battery backup system</Link>, you need a dedicated electrician who can coordinate all the work under a single permit.
          </p>

          <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Our Solar Electrical Services in Fresno</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { label: 'Solar Panel Electrical Connections', path: '/services/renewables-ev/solar-panel-electrical-connections', desc: 'Inverter wiring, panel tie-in, PG&E interconnection' },
              { label: 'Solar Inverter Installation', path: '/services/renewables-ev/solar-inverter-installation', desc: 'String, micro, and hybrid inverter installation Fresno' },
              { label: 'Solar Battery Storage', path: '/services/renewables-ev/solar-battery-storage-installation', desc: 'Powerwall & Enphase battery installation in Fresno' },
              { label: 'Solar System Monitoring', path: '/services/renewables-ev/solar-system-monitoring-setup', desc: 'Track your solar production from your phone' },
            ].map(s => (
              <Link key={s.path} to={s.path} className="flex items-start gap-4 p-4 border-2 border-gray-100 hover:border-[#FFD700] rounded-xl transition-all cursor-pointer group">
                <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <i className="ri-arrow-right-circle-fill text-[#FFD700] text-xl"></i>
                </span>
                <div>
                  <p className="font-black text-[#1a1a1a] text-sm">{s.label}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 bg-[#1a1a1a] rounded-2xl p-8 sm:p-10">
            <h3 className="text-2xl font-black text-white mb-3">Get a Solar Electrical Quote in Fresno</h3>
            <p className="text-gray-400 text-sm mb-6">From panel upgrades to battery storage and PG&E interconnection, our licensed Fresno electricians handle all solar electrical work with permits guaranteed.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-black text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all cursor-pointer whitespace-nowrap">
              Get Solar Electrical Quote <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </article>

        <div className="max-w-[900px] mx-auto px-6 pb-16"><QuoteFormWidget /></div>
        <Footer />
      </div>
    </>
  );
};

export default SolarBlogPost;
